import { TimeReportingFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { DateHelper, UtilsHelper } from "utils/helpers";
import * as Yup from "yup";
import { WintSuperkollAPIModel, WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import { TimeReportingEnums } from "models/enums";
import { SalaryDeviationsService } from "services";
import { formatHours, roundHours } from "./formatHours";

class TimeReportHelper {
  validateDay = (context: Yup.TestContext, earliestDate: Date, val: TimeReportingFormTypes.HoursByDay) => {
    const duplicateCount: number = 1;
    let earliest: Date = DateHelper.resetTime(new Date()) as Date;
    let valDayDate: Date = DateHelper.resetTime(new Date()) as Date;

    earliest = DateHelper.resetTime(new Date(earliestDate)) as Date;
    valDayDate = DateHelper.resetTime(new Date(val?.day as Date)) as Date;

    if (val !== undefined && val.day !== undefined) {
      if (DateHelper.isBefore(valDayDate, earliest)) {
        return context.createError({
          message: TranslationKeys.Validations.InvalidDate,
          path: `${context.path}.day`,
        });
      }
      const ct = context.parent.filter((d: TimeReportingFormTypes.HoursByDay) =>
        DateHelper.isEqual(DateHelper.resetTime(d.day) as Date, DateHelper.resetTime(valDayDate) as Date)
      ).length;
      if (ct > duplicateCount) {
        return context.createError({
          message: TranslationKeys.TimeReporting.DuplicateOfDay,
          path: `${context.path}.day`,
        });
      }
    }
    return true;
  };

  calculteProjectHours = (
    weekName: string,
    rows: TimeReportingFormTypes.MonthlyTimeReportRow[],
    projectTimes?: ApiModel.ProjectTime[]
  ) => {
    projectTimes?.forEach((proj) => {
      proj.categoryTimes?.forEach((ct) => {
        const row = rows.find(
          (r) => r.projectId === proj.projectId && r.categoryId === ct.categoryId
        ) as TimeReportingFormTypes.MonthlyTimeReportRow;
        if (row) {
          (row as any)[weekName] = ((row as any)[weekName] ?? 0) + ct.hours;
        } else {
          rows.push({
            type: TimeReportingEnums.TimeReportType.project,
            name: proj.projectName,
            categoryName: ct.categoryName,
            categoryId: ct.categoryId,
            [weekName]: ct.hours,
            projectId: proj.projectId,
            isInternal: proj.isInternal,
            uniqueId: UtilsHelper.getUniqueId(),
          } as TimeReportingFormTypes.MonthlyTimeReportRow);
        }
      });
    });

    rows = rows.sort((a, b) => {
      if (`${a.name}${a.categoryName}` < `${b.name}${b.categoryName}`) {
        return -1;
      }

      if (`${a.name}${a.categoryName}` > `${b.name}${b.categoryName}`) {
        return 1;
      }

      return 0;
    });
  };

  calculateDeviationHours = (
    weekName: string,
    rows: TimeReportingFormTypes.MonthlyTimeReportRow[],
    deviations?: ApiModel.TimeReportDeviation[]
  ) => {
    deviations?.forEach((deviation) => {
      const row = rows.find((r) => this.isExistingMonthlyDeviationRow(deviation, r)) as any;
      if (row) {
        row[weekName] = (row[weekName] ?? 0) + deviation.hours;
      } else {
        const name = SalaryDeviationsService.getSalaryDeviationTypeText(
          deviation.deviationType,
          deviation.customDeviationName
        );
        rows.push({
          type: TimeReportingEnums.TimeReportType.deviation,
          name: name,
          customName: deviation.customDeviationName ?? "",
          [weekName]: deviation.hours,
          deviationId: deviation.deviationId,
          deviationType: deviation.deviationType,
          uniqueId: UtilsHelper.getUniqueId(),
        } as TimeReportingFormTypes.MonthlyTimeReportRow);
      }
    });
  };

  isExistingMonthlyDeviationRow = (
    deviation: ApiModel.TimeReportDeviation,
    row: TimeReportingFormTypes.MonthlyTimeReportRow
  ) => {
    if (row.deviationType !== deviation.deviationType) {
      return false;
    }
    if (deviation.deviationType === WintSuperkollAPIModel.SalaryDeviationType.CustomTimeDeviation) {
      return deviation.customDeviationName === row.customName;
    }
    return true;
  };

  isExistingWeeklyDeviationRow = (
    deviation: ApiModel.TimeReportDeviation,
    row: TimeReportingFormTypes.WeeklyTimeReportRow
  ) => {
    if (row.deviationType !== deviation.deviationType) {
      return false;
    }
    if (deviation.deviationType === WintSuperkollAPIModel.SalaryDeviationType.CustomTimeDeviation) {
      return deviation.customDeviationName === row.customName;
    }
    return row.childName === deviation.childName;
  };

  roundHours = roundHours;

  formatHours = formatHours;

  isWeekFallInPeriod = (week: ApiModel.Week, periodStart?: Date, periodEnd?: Date) => {
    const isWeekStartBeforePeriodEnd = periodEnd
      ? DateHelper.isBefore(new Date(week.weekStart), new Date(periodEnd)) ||
        DateHelper.isEqual(new Date(week.weekStart), new Date(periodEnd))
      : true;

    const isPeriodStartBeforeWeekEnd = periodStart
      ? DateHelper.isBefore(new Date(periodStart), new Date(week.weekEnd)) ||
        DateHelper.isBefore(new Date(periodStart), new Date(week.weekEnd))
      : true;

    return isWeekStartBeforePeriodEnd && isPeriodStartBeforeWeekEnd;
  };
}

const TimeReportHelperInstance = new TimeReportHelper();
export default TimeReportHelperInstance;
